import React from 'react';
import { Link } from 'react-router-dom';

const Handyman = () => {
  return (
    <div className="min-h-screen bg-white">
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-navy-800 via-navy-900 to-cyan-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-400 rounded-full blur-3xl"></div>
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl text-white">
            <div className="inline-block bg-cyan-400/20 backdrop-blur-sm border border-cyan-400/40 rounded-full px-6 py-2 mb-6">
              <span className="text-sm font-bold tracking-wide">SKILLED TECHNICIANS</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black leading-none mb-6">
              Handyman<br />
              <span className="text-cyan-400">Services</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              Expert repairs, installations, and maintenance for your home or business.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/get-quote" className="px-8 py-4 bg-cyan-400 text-navy-900 font-bold rounded-xl hover:bg-cyan-500">
                REQUEST SERVICE
              </Link>
              <a href="#services" className="px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-xl hover:bg-white/10">
                VIEW SERVICES
              </a>
            </div>
          </div>
        </div>
      </section>

      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {[
              {
                category: 'Repairs & Fixes',
                services: [
                  'Drywall repair and patching',
                  'Door and window repairs',
                  'Furniture assembly and repair',
                  'Plumbing fixes (leaks, faucets, toilets)',
                  'Electrical repairs (switches, outlets)',
                  'Appliance installation',
                  'Cabinet repair and adjustment',
                  'Deck and fence repairs',
                ],
                icon: '🔧',
                color: 'from-cyan-400 to-cyan-600',
              },
              {
                category: 'Installations',
                services: [
                  'TV mounting and setup',
                  'Shelf and bracket installation',
                  'Light fixture installation',
                  'Ceiling fan installation',
                  'Blinds and curtain rod installation',
                  'Door hardware installation',
                  'Bathroom fixture installation',
                  'Smart home device setup',
                ],
                icon: '⚙️',
                color: 'from-navy-700 to-navy-900',
              },
              {
                category: 'Maintenance',
                services: [
                  'Gutter cleaning and repair',
                  'Pressure washing',
                  'HVAC filter replacement',
                  'Weatherproofing and caulking',
                  'Smoke detector installation',
                  'Regular property maintenance',
                  'Seasonal home prep',
                  'Preventive maintenance checks',
                ],
                icon: '🛠️',
                color: 'from-cyan-500 to-navy-700',
              },
              {
                category: 'Improvements',
                services: [
                  'Painting and touch-ups',
                  'Tile work and grouting',
                  'Flooring installation',
                  'Trim and molding installation',
                  'Kitchen and bathroom updates',
                  'Lighting upgrades',
                  'Storage solutions',
                  'Accessibility modifications',
                ],
                icon: '✨',
                color: 'from-coral-400 to-coral-500',
              },
            ].map((section, index) => (
              <div key={index} className="relative group">
                <div className={`absolute inset-0 bg-gradient-to-br ${section.color} rounded-2xl opacity-5 group-hover:opacity-10`}></div>
                <div className="relative bg-white rounded-2xl p-8 border-2 border-gray-200 group-hover:border-cyan-400 shadow-lg">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="text-5xl">{section.icon}</div>
                    <h3 className="text-3xl font-black text-navy-900">{section.category}</h3>
                  </div>
                  <ul className="space-y-3">
                    {section.services.map((service, sIndex) => (
                      <li key={sIndex} className="flex items-start">
                        <span className="w-2 h-2 bg-cyan-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span className="text-gray-700">{service}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-black text-navy-900 mb-6">
              Why Choose Our <span className="text-cyan-500">Handyman Team</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              {[
                { title: 'Skilled Professionals', desc: 'Experienced technicians with diverse expertise', icon: '👷' },
                { title: 'Fair Pricing', desc: 'Transparent rates with no hidden fees', icon: '💵' },
                { title: 'Quality Guaranteed', desc: '100% satisfaction on every job', icon: '✅' },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200"
                >
                  <div className="text-4xl mb-3">{item.icon}</div>
                  <h3 className="text-xl font-bold text-navy-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-navy-900 to-cyan-900 text-white">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-6">
            Got a Project? Let's Get It Done.
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            From small repairs to major installations, we handle it all
          </p>
          <Link to="/get-quote" className="inline-block px-12 py-5 bg-cyan-400 text-navy-900 font-black text-lg rounded-xl hover:bg-cyan-500 shadow-2xl">
            BOOK A HANDYMAN
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Handyman;
